import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import PromptDialog from './PromptDialog'
import tokens from './tokens'
import OfoqPasswordField from '../../components/Forms/OfoqPasswordField'
import UseTranslation from '../../hooks/UseTranslation'

// Whole sentences per biometry type rather than one sentence with the label
// interpolated: Arabic does not keep the English word order, so a split string
// leaves the label stranded mid-phrase.
const COPY = {
  face: {
    title: 'Enable Face Login?',
    body: "You'll be able to sign in to Orgarise with face recognition instead of your password. You can turn this off anytime from Settings.",
  },
  fingerprint: {
    title: 'Enable Fingerprint Login?',
    body: "You'll be able to sign in to Orgarise with your fingerprint instead of your password. You can turn this off anytime from Settings.",
  },
}

/**
 * Enable confirmation, in the prototype's dialog shell.
 *
 * The password field is the one addition to that design: the vault replays a
 * real sign-in, so the password is what gets stored behind the sensor, and
 * asking for it re-authenticates whoever is holding the unlocked phone.
 */
const EnableBiometricDialog = ({ open, type, loading, error, onConfirm, onCancel }) => {
  const { t } = UseTranslation()
  const [password, setPassword] = useState('')
  const copy = COPY[type]

  useEffect(() => {
    if (open) setPassword('')
  }, [open])

  if (!copy) return null

  return (
    <PromptDialog
      open={open}
      icon={type}
      title={t?.[copy.title] || copy.title}
      body={t?.[copy.body] || copy.body}
      cancelText={t?.Cancel || 'Cancel'}
      confirmText={t?.Enable || 'Enable'}
      loading={loading}
      confirmDisabled={!password}
      onConfirm={() => onConfirm(password)}
      onCancel={onCancel}
    >
      {/* The app's password input, so this reads and behaves exactly like the
          one in Change Password - same label, same show/hide eye. The strength
          checklist is suppressed: this confirms an existing password rather
          than choosing a new one. */}
      <Box sx={styles.field}>
        <OfoqPasswordField
          name={t?.['Confirm your password'] || 'Confirm your password'}
          placeholder={t?.['Enter your password'] || 'Enter your password'}
          defaultValue={password}
          refState={setPassword}
          hideInputValidator
          error={Boolean(error)}
          helperText={error || ''}
        />
      </Box>
    </PromptDialog>
  )
}

const styles = {
  // The dialog body centres its children; the field and its label read left.
  field: {
    width: '100%',
    textAlign: 'start',
    '& .password-label': {
      fontSize: '13px',
      color: tokens.color.textSecondary,
      marginBottom: '8px',
    },
    '& .MuiFormHelperText-root': {
      marginInlineStart: 0,
      fontSize: '12px',
    },
  },
}

export default EnableBiometricDialog
